// Find campaigns (Find walk-through, 19 Oct 2026; docs/specs/FIND_WALKTHROUGH_2026_10.md § Campaigns contract).
// A campaign is a Find play (table `plays`, module 'find'): who to target (audience), the offer (card), its own
// sequence, its approvals and its share of the weekly pace. K-08 builds the database, act and demo side and owns this
// file; K-09, K-10, K-12 and K-15 call these functions and never change their shapes. A change to a shape is agreed
// with the coordinator first.
import type { Crm } from "./crm";
import type { ContactGrade } from "./prospects";
import type { EmailSource } from "./emailSources";
import type { JsonObject } from "./types";

export type CampaignStatus = "draft" | "proposed" | "approved" | "paused" | "retired";

export interface CampaignStep {
  /** Days after the previous step (0 for the first). */
  day: number;
  channel: "email" | "by_hand" | "social_dm" | "call" | "letter" | "linkedin";
  /** What this step is for, in a few words ("First email", "Follow-up with a case study"). */
  label?: string;
}

/** Per-campaign approvals: the same keys as Rules › Approvals and spend; a missing key follows the Rules default. */
export type CampaignApprovals = Record<string, "ask" | "auto">;

/** Who a campaign is for (10b, 30 Oct 2026): new firms (Find), firms you know (always on, by a rule) or both. */
export type CampaignReach = "new" | "known" | "both";

/** Which firms you know a rule covers. */
export type KnownFirms = { kind: "all" } | { kind: "stage" | "tag" | "tier"; value: string };

/** The rule of a campaign for firms you know: firms last spoken to more than quiet_days ago, which firms, the topic (in
 *  place of an offer) and its own daily limit, apart from the new-business pace. */
export interface KnownRule {
  quiet_days: number;
  firms: KnownFirms;
  topic?: string;
  daily_limit: number;
}

/** 2.30 When a campaign runs (migration 20270311090100). */
export interface CampaignTiming {
  /** Its first day (YYYY-MM-DD); none is as soon as possible. */
  starts_on?: string;
  /** The time of day new firms' first drafts are ready (HH:MM, the business's time); none is whenever the engine runs. */
  drafts_at?: string;
  /** Its own sending window; none is the business's. */
  send_window?: { windows: { days: string | string[]; start: string; end: string }[]; timezone?: string };
}

export interface Campaign {
  id: string;
  /** Absent on an older database: new firms. */
  reach?: CampaignReach;
  /** For firms you know and both; absent for new firms. */
  known_rule?: KnownRule | null;
  name: string;
  status: CampaignStatus;
  /** Percent of the weekly pace (all approved campaigns' shares add up to 100). */
  share: number;
  audience: JsonObject;
  /** The offer card; `card.cadence_plan` holds the guided cadence answers (Rules › Campaigns). */
  card: JsonObject;
  steps: CampaignStep[];
  approvals: CampaignApprovals;
  /** 2.30 When it was first approved (null until then, and again after it is reactivated). */
  launched_at?: string | null;
  /** When it was made. */
  created_at?: string | null;
  /** The prospect search the campaign was made from, if any. */
  search_id: string | null;
  /** The learning base: what the owner told Ogma this campaign should say, read into chips (lib/learning.ts readBrief). */
  brief?: JsonObject | null;
  /** When it runs (crm_campaign_set_timing): absent fields use the business's. */
  timing?: CampaignTiming;
  /** A change to who a running campaign targets or its offer, waiting for the owner ({audience?, card?, by, at}). */
  pending?: JsonObject | null;
  counts: {
    planned: number;
    in_sequence: number;
    engaged: number;
    drafts_waiting: number;
    hand_waiting: number;
    calls_waiting: number;
    /** Messages that went out for this campaign, by email or by hand (fix plan PR 1). */
    sent: number;
    /** 10b: firms its rule added (being followed up), and firms due under the rule now. */
    following_up?: number;
    due?: number;
  };
  updated_at: string;
}

/** finished: the sequence ran to its end with no reply (28 Oct 2026); stopped: the owner or a rule stopped the firm. */
/** following_up: added by a campaign's rule for firms you know (10b). */
export type LongListStatus = "planned" | "in_sequence" | "delayed" | "engaged" | "finished" | "stopped" | "following_up";

export interface LongListRow {
  organisation_id: string;
  name: string;
  country: string | null;
  fit_score: number | null;
  /** 1 = contacted first. */
  position: number;
  status: LongListStatus;
  /** Sequence step reached (0 = not started) and its channel. */
  step_no: number;
  step_channel: CampaignStep["channel"] | null;
  /** When the first touch is due (planned) or the next step is due (in sequence). */
  due_at: string | null;
  delayed_until: string | null;
  /** 27 Oct 2026 Where the address the firm will be emailed at came from (crm__org_email_source). */
  email_source?: EmailSource | null;
  /** How Find can reach the firm (ref.search_firms): A a personal email, B a general inbox, C social or phone, M by hand. */
  grade?: ContactGrade | null;
  /** 10b When a campaign's rule last added the firm. */
  followed_up_at?: string | null;
  /** 28 Oct 2026 What was sent to the firm, in any campaign (crm__org_touches_sent); absent when nothing was. */
  touches?: number;
  first_contacted_at?: string;
  last_contacted_at?: string;
}


export interface OverviewChannel {
  channel: CampaignStep["channel"];
  waiting: number;
  sent_today: number;
  due_tomorrow: number;
  /** Things that need the owner ("mailbox not connected", "3 drafts waiting over a day"). */
  flags: string[];
}

export interface CampaignOverview {
  campaign_id: string | null;
  name: string;
  channels: OverviewChannel[];
}

export interface CampaignInput {
  id?: string;
  name: string;
  reach?: CampaignReach;
  known_rule?: Partial<KnownRule> | null;
  audience?: JsonObject;
  card?: JsonObject;
  steps?: CampaignStep[];
  approvals?: CampaignApprovals;
  share?: number;
}

// ---------------------------------------------------------------- reads (crm.read adds p_tenant)
export const campaigns = (crm: Crm) => crm.read<Campaign[]>("crm_campaigns", {});
/** A campaign's long list; null for the main campaign (or, with no campaigns, the firms lined up from Find). */
export const campaignLongList = (crm: Crm, campaign: string | null) => crm.read<LongListRow[]>("crm_campaign_long_list", { p_campaign: campaign });
export const campaignOverview = (crm: Crm) => crm.read<CampaignOverview[]>("crm_campaign_overview", {});

// ---------------------------------------------------------------- writes (act group "campaigns", K-08)
/** What saving returns: the campaign, its status, and whether a change waits for the owner (a running campaign keeps running). */
export interface SaveCampaignResult {
  id: string;
  status?: CampaignStatus;
  pending?: boolean;
}

/**
 * Create or change a campaign (a draft until approved). A running campaign keeps running: name, steps, approvals, share
 * and card.cadence_plan apply at once; a new audience or offer applies at once unless the business gates it, when it
 * waits as `pending` until the owner approves it (decideCampaign "approve").
 */
export const saveCampaign = (crm: Crm, c: CampaignInput) => crm.write<SaveCampaignResult>("save_campaign", { campaign: c });
/** A copy to edit, as a draft. */
export const copyCampaign = (crm: Crm, id: string, name: string) => crm.write<{ id: string }>("copy_campaign", { id, name });
/** Approve, pause, resume or retire (approve goes through the usual human gate when the business has one). */
export const decideCampaign = (crm: Crm, id: string, decision: "approve" | "pause" | "resume" | "retire") =>
  crm.write<{ status: CampaignStatus }>("decide_campaign", { id, decision });
/** Bring back an archived (retired) campaign: it waits for approval again, or stays a draft until its offer is finished. */
export const reactivateCampaign = (crm: Crm, id: string) => crm.write<{ status: CampaignStatus; proposed: boolean }>("reactivate_campaign", { id });
/** Shares of the weekly pace, percent per campaign id; they must add up to 100. */
export const setCampaignShares = (crm: Crm, shares: Record<string, number>) => crm.write<{ ok: true }>("set_campaign_shares", { shares });
/** How far a campaign made from a search has its list in: `next` of `total` of the search's firms (fit order). */
export interface CampaignFill {
  added: number;
  next: number;
  total: number;
}
/** A draft campaign from a prospect search ("Run a campaign"), with the first part of its list; Ogma adds the rest in
 *  the background (the Find worker's campaign-fills sweep, 100 firms a call). */
export const campaignFromSearch = (crm: Crm, search: string) => crm.write<{ id: string } & CampaignFill>("campaign_from_search", { search });
/** Start the campaign by itself once its search is fully scored (on), or not; already scored, it starts now. */
export const campaignStartWhenScored = (crm: Crm, id: string, on: boolean) =>
  crm.write<{ started: boolean; status?: CampaignStatus }>("campaign_start_when_scored", { id, on });

/** A campaign made from a search, for its page (crm_campaign_search_list). */
export interface CampaignSearchList {
  search_id: string;
  query: string;
  ranked_at: string | null;
  /** The search has qualities to score; without them its list is final at once. */
  ranks: boolean;
  recorded: number;
  scored: number;
  on_list: number;
  fill_next: number | null;
  fill_total: number;
  blocked: { kind: "credits" | "cap" | "month"; until: string } | null;
  start_when_scored: boolean;
  firms: { organisation_id: string; name: string; position: number; status: LongListStatus; score: number | null; reason: string | null }[];
}
export const campaignSearchList = (crm: Crm, campaign: string, limit = 20) =>
  crm.read<CampaignSearchList | null>("crm_campaign_search_list", { p_campaign: campaign, p_limit: limit });
/** Move a firm to a new position in the campaign's queue (1 = first); campaign null is the main campaign. */
export const reorderCampaignFirm = (crm: Crm, campaign: string | null, organisation: string, position: number) =>
  crm.write<{ ok: true }>("reorder_campaign_firm", { campaign, organisation, position });
/** Delay: the firm goes back to the Long list, not contacted before `days` from now (default 30). */
export const delayCampaignFirm = (crm: Crm, campaign: string | null, organisation: string, days?: number) =>
  crm.write<{ ok: true; delayed_until: string }>("delay_campaign_firm", { campaign, organisation, days });

// ---------------------------------------------------------------- Prospects › Lined up › Add a firm (2.21, 22 Oct 2026)
/** One firm in Ogma's database, as crm_find_targets shows it (migration 20270202090000). */
export interface TargetPerson {
  person_id: string;
  name: string;
  role: string;
  title?: string;
  ownership_band?: string;
  emails?: { email: string; status: string; basis?: string }[];
}
export interface TargetFirm {
  ref_firm_id: string;
  name: string;
  status: "active" | "dormant" | "closed" | "unknown";
  /** How the search found it. */
  matched_on: "company_number" | "name" | "person";
  company_number?: string;
  city?: string;
  postcode?: string;
  region?: string;
  website?: string;
  domain?: string;
  people: TargetPerson[];
  /** The firm's own addresses and phone numbers. */
  emails?: string[];
  phones?: string[];
  /** Where it already is in this business's CRM, if anywhere. */
  crm?: { organisation_id: string; stage?: string; module?: string; campaign?: { id: string; name: string; position: number; status: string } };
}
/** The person to write to, when the database has no address for them. */
export interface TargetContact {
  name: string;
  email: string;
  role?: string;
}

/** Look a firm up by its name, its company number or the name of someone who runs it (three letters at least). */
export const findTargets = (crm: Crm, query: string) => crm.read<{ query: string; firms: TargetFirm[] }>("crm_find_targets", { p_query: query });
/** Put one database firm first in a campaign's queue, with the person to write to; `ready` when someone has an email. */
export const addTargetToCampaign = (crm: Crm, campaign: string, firm: string, contact: TargetContact | null) =>
  crm.write<{ organisation_id: string; person_id?: string; position: number; ready: boolean }>("add_target_to_campaign", { campaign, firm, contact });

// ---------------------------------------------------------------- added by L-05 (round 2; act group "find_r2_l05")
/**
 * Approve or reject a waiting change to who a running campaign targets or its offer (the approve_campaign_change task).
 * Approve applies it (as decideCampaign "approve"); reject drops it and the campaign keeps running as it was.
 */
export const decideCampaignChange = (crm: Crm, id: string, decision: "approve" | "reject") =>
  crm.write<{ status: CampaignStatus; applied: boolean }>("decide_campaign_change", { id, decision });
